<script setup lang="ts">
/**
 * ResetPasswordModal — admin-side reset of another user's password.
 *
 * Distinct from ChangePasswordModal (current user changes own
 * password). This flow doesn't require knowing the current
 * password — the admin is taking the user's word that the reset
 * is needed.
 *
 * Two fields: newPassword + confirmPassword. Confirm is client-side
 * only and dropped before submitting; backend only sees the new
 * password.
 */
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToastStore } from '@/stores/toast'
import Modal from '@/components/ui/Modal.vue'
import Button from '@/components/ui/Button.vue'
import Input from '@/components/ui/Input.vue'
import FormRow from '@/components/ui/FormRow.vue'
import { useApiForm } from '@/composables/useApiForm'
import { passwordResetSchema } from '../schemas'
import { useResetPasswordMutation } from '../queries'
import type { UserView } from '../api'

interface Props {
  user: UserView | null
}
const props = defineProps<Props>()

const open = defineModel<boolean>('open', { required: true })

const { t } = useI18n()
const toast = useToastStore()

const userName = computed(() => props.user?.userName ?? '')
const reset = useResetPasswordMutation(userName.value)

const form = useApiForm({
  schema: passwordResetSchema,
  initialValues: { newPassword: '', confirmPassword: '' },
  submit: async (values) => {
    // Drop the confirm field before sending — it's client-only.
    return reset.mutateAsync({ newPassword: values.newPassword })
  },
  onSuccess: () => {
    toast.push({
      kind: 'success',
      message: t('iam.resetPassword.success', { name: userName.value }),
    })
    open.value = false
  },
})

// Reset the form when a different user is targeted, or when the
// modal closes (so reopening it shows blank fields).
watch(
  [() => props.user, open],
  ([, isOpen]) => {
    if (!isOpen) form.resetForm()
  },
)

const [newPassword] = form.defineField('newPassword')
const [confirmPassword] = form.defineField('confirmPassword')

function close(): void {
  open.value = false
}
</script>

<template>
  <Modal
    v-model:open="open"
    :title="$t('iam.resetPassword.title')"
    :description="$t('iam.resetPassword.description', { name: userName })"
    size="md"
  >
    <form v-if="user" @submit.prevent="form.submit()">
      <FormRow
        :label="$t('iam.resetPassword.newPassword.label')"
        :helper="$t('iam.resetPassword.newPassword.helper')"
        :error="form.errors.value.newPassword"
        required
      >
        <Input
          v-model="newPassword"
          type="password"
          :error="form.errors.value.newPassword"
        />
      </FormRow>

      <FormRow
        :label="$t('iam.resetPassword.confirmPassword.label')"
        :error="form.errors.value.confirmPassword"
        required
      >
        <Input
          v-model="confirmPassword"
          type="password"
          :error="form.errors.value.confirmPassword"
        />
      </FormRow>
    </form>

    <template #footer>
      <Button kind="ghost" @click="close">
        {{ $t('actions.cancel') }}
      </Button>
      <Button
        kind="primary"
        type="submit"
        :loading="form.isSubmitting.value"
        @click="form.submit()"
      >
        {{ $t('iam.resetPassword.submit') }}
      </Button>
    </template>
  </Modal>
</template>
